import React, { useState, useEffect, useMemo } from 'react';
import { getPostPurchaseRecommendedProducts } from '../../api/lastupsell';
import { Offer, SmartProduct } from '../../types';
import ProductOffer from '../ProductOffer';
import { SkeletonOffer } from '../elements';
import { SmartUpsellProductOfferProps } from './SmartUpsellProductOfferProps';
import { usePostPurchaseContext } from '../../contexts/PostPurchaseContext';
import { distributeOffersToSmartProducts } from '../../api/shared-app-requests';

const SmartUpsellProductOffer = ({
  smartOffers,
  offers,
  order,
  offerCount,
  ...restProps
}: SmartUpsellProductOfferProps) => {
  const { lineItems } = usePostPurchaseContext();
  const [recommendedProducts, setRecommendedProducts] =
    useState<SmartProduct[]>();

  let limit = useMemo(
    () =>
      smartOffers.reduce((acc: number, curr: Offer) => {
        return acc + curr.recommended_product_count;
      }, 0),
    [smartOffers],
  );
  limit = limit > 10 ? 10 : limit;

  useEffect(() => {
    async function fetchAndDoStuffWithData() {
      const productId = lineItems && lineItems[0].product_id;
      if (!productId) return;
      const recommendedProducts = await getPostPurchaseRecommendedProducts(
        productId,
        limit,
      );
      // recommended products without ones already offered by ProductOffers and purchased
      let filteredRecommendedProducts = recommendedProducts
        .filter((product: any) => {
          const productInOffers = offers.find(
            (offer) => product.handle === offer.shopify_product_handle,
          );
          const alreadyPurchasedProduct = lineItems?.find(
            (item) => product.id === item.product_id,
          );
          return !productInOffers && !alreadyPurchasedProduct;
        })
        .slice(0, limit);
      filteredRecommendedProducts = distributeOffersToSmartProducts(
        filteredRecommendedProducts,
        smartOffers,
      );
      setRecommendedProducts(filteredRecommendedProducts);
    }

    fetchAndDoStuffWithData();
  }, [limit, lineItems, offers, smartOffers]);

  const skeletonCount = Array.from({ length: limit }, (v, k) => k + 1);

  return (
    <>
      {!recommendedProducts
        ? skeletonCount.map((key) => (
            <SkeletonOffer
              indexInOffers={order}
              key={key}
              offerCount={offerCount}
            />
          ))
        : recommendedProducts.map((product) => (
            <ProductOffer
              key={product.id}
              indexInOffers={order}
              offerCount={offerCount}
              offer={product.offer}
              {...restProps}
              currentProduct={product}
            />
          ))}
    </>
  );
};

export default SmartUpsellProductOffer;
